import type { CapitalGlobalProposalSubStipulatorAddressValues } from '@/features/proposals/schemas/capital-global/proposal-step-dados-do-seguro.schema';
import type { Proposal, ProposalCustomer } from '@/services/proposals/types';

function normalizeDocument(value?: string) {
  return (value || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

function toSubStipulantAddress(
  customer: ProposalCustomer
): CapitalGlobalProposalSubStipulatorAddressValues {
  const documentNumber = customer.documentNumber || customer.cpf || '';

  return {
    customerPk: customer.pk,
    documentNumber,
    sequenceNumber: customer.sequenceNumber,
    name: customer.name || customer.socialName || '',
    // `isOrgParty` é o discriminador correto: CAEPF também tem 14 dígitos, então o
    // tamanho do documento sozinho classificaria pessoa física como empresa.
    isCompany:
      customer.isOrgParty === 'Y' ||
      (customer.isOrgParty === undefined &&
        normalizeDocument(documentNumber).length === 14),
    cep: customer.zipCode || '',
    street: customer.address || '',
    number: customer.addressNumber || '',
    complement: customer.addressComplement || '',
    neighborhood: customer.neighborhood || '',
    city: customer.city || '',
    state: customer.state || '',
    phone: customer.cellphone || customer.phone || '',
    email: customer.email || '',
    gender: customer.genderCode || '',
    dateOfBirth: (customer.dateOfBirth || '').split('T')[0] || '',
  };
}

/**
 * Linhas de endereço dos SUBESTIPULANTES (`sequenceNumber` ≥ 1) a partir da proposta.
 * O estipulante fica de fora — o endereço dele é o bloco `registration*` do Step 1.
 *
 * Os campos de endereço fazem round-trip pelo InsureMO (`Bairro/CEP/Endereco/...` por
 * customer), então reabrir a proposta traz o que já foi salvo.
 */
export function mapProposalCustomersToSubStipulantAddresses(
  proposal: Proposal | undefined
): CapitalGlobalProposalSubStipulatorAddressValues[] {
  const customers = proposal?.customers;
  if (!customers?.length) return [];

  return customers
    .filter(customer => (customer.sequenceNumber ?? 0) > 0)
    .slice()
    .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
    .map(toSubStipulantAddress);
}

/**
 * Sobrepõe o rascunho da sessão às linhas vindas da proposta, casando por DOCUMENTO.
 * Casar por índice corromperia os dados se a lista de subs tiver mudado na Cotação
 * entre uma visita e outra ao Step 1.
 */
export function mergeSubStipulantAddressesDraft(
  rows: CapitalGlobalProposalSubStipulatorAddressValues[],
  draft: CapitalGlobalProposalSubStipulatorAddressValues[] | undefined
): CapitalGlobalProposalSubStipulatorAddressValues[] {
  if (!draft?.length) return rows;

  const draftByDocument = new Map(
    draft.map(entry => [normalizeDocument(entry.documentNumber), entry])
  );

  return rows.map(row => {
    const match = draftByDocument.get(normalizeDocument(row.documentNumber));
    if (!match) return row;
    // Identidade (pk/seq/nome/tipo) sempre da proposta — o rascunho manda só nos dados.
    return {
      ...match,
      customerPk: row.customerPk,
      sequenceNumber: row.sequenceNumber,
      name: row.name,
      documentNumber: row.documentNumber,
      isCompany: row.isCompany,
    };
  });
}

/** Linha ainda sem endereço utilizável — dispara a resolução automática. */
export function isSubStipulantAddressIncomplete(
  entry: CapitalGlobalProposalSubStipulatorAddressValues
): boolean {
  return (
    !entry.street.trim() ||
    !entry.neighborhood.trim() ||
    !entry.city.trim() ||
    !entry.state.trim()
  );
}
